/**
 * Speak Exercise (INT-CMP-002)
 * Open-ended speaking exercise where user speaks a response.
 * Similar to RepeatAfter but with a free-form prompt instead of a target phrase.
 */

import React, { useState, useCallback } from 'react';
import { TouchableOpacity } from 'react-native';
import { Box, Text, VStack, HStack } from '@gluestack-ui/themed';
import { FS_XS, FS_SM, FS_MD } from '../../theme';
import { useExerciseScoring } from '../../hooks/useExerciseScoring';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

interface SpeakExerciseProps {
  prompt: string;
  expectedKeywords?: string[];
  hint?: string;
  voiceEnabled: boolean;
  onComplete: (correct: boolean, score: number) => void;
}

export const SpeakExercise = React.memo(function SpeakExercise({
  prompt,
  expectedKeywords,
  hint,
  voiceEnabled,
  onComplete,
}: SpeakExerciseProps) {
  const { theme: t, fontScale, ffs } = useDisplayTheme();

  const [completed, setCompleted] = useState(false);
  const [score, setScore] = useState<number | null>(null);

  const handleSelfScore = useCallback((selfScore: number) => {
    setScore(selfScore);
    setCompleted(true);
    onComplete(selfScore >= 70, selfScore);
  }, [onComplete]);

  // Keyboard shortcuts: 1/2/3 keys for self-scoring (web only, no-op on native)
  const showScoring = !completed && !voiceEnabled;
  useExerciseScoring([40, 70, 95], handleSelfScore, showScoring);

  return (
    <Box
      p="$4"
      rounded="$lg"
      style={{ backgroundColor: t.card, borderWidth: 1, borderColor: t.border }}
    >
      <VStack space="md">
        {/* Prompt */}
        <Box
          p="$4"
          rounded="$lg"
          style={{ backgroundColor: t.primarySoft }}
        >
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_MD * fontScale, fontWeight: '600', color: t.primary, textAlign: 'center', ...ffs }}
          >
            {prompt}
          </Text>
        </Box>

        {expectedKeywords && expectedKeywords.length > 0 && (
          <VStack space="xs">
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_XS * fontScale, color: t.textSecondary, ...ffs }}
            >
              Try to include:
            </Text>
            <HStack space="xs" flexWrap="wrap">
              {expectedKeywords.map((kw, i) => (
                <Box
                  key={i}
                  px="$2"
                  py="$0.5"
                  rounded="$full"
                  style={{ backgroundColor: t.isDark ? '#374151' : '#F3F4F6' }}
                >
                  <Text
                    allowFontScaling={false}
                    style={{ fontSize: FS_XS * fontScale, color: t.text, ...ffs }}
                  >
                    {kw}
                  </Text>
                </Box>
              ))}
            </HStack>
          </VStack>
        )}

        {hint && !completed && (
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: t.textSecondary, fontStyle: 'italic', ...ffs }}
          >
            {hint}
          </Text>
        )}

        {/* Self-assessment when voice is not enabled */}
        {!completed && !voiceEnabled && (
          <VStack space="sm">
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_XS * fontScale, color: t.textSecondary, textAlign: 'center', ...ffs }}
            >
              Practice speaking your response, then rate yourself:
            </Text>
            <HStack space="sm" justifyContent="center">
              {[
                { label: 'Needs work', score: 40, color: '#DC2626' },
                { label: 'Getting there', score: 70, color: '#D97706' },
                { label: 'Nailed it!', score: 95, color: '#059669' },
              ].map((option) => (
                <TouchableOpacity
                  key={option.label}
                  testID={`speak-self-score-${option.score}`}
                  accessibilityLabel={option.label}
                  accessibilityRole="button"
                  onPress={() => handleSelfScore(option.score)}
                  activeOpacity={0.7}
                >
                  <Box
                    px="$3"
                    py="$2"
                    rounded="$md"
                    style={{ backgroundColor: t.isDark ? '#374151' : '#F3F4F6', borderWidth: 1, borderColor: t.border }}
                  >
                    <Text
                      allowFontScaling={false}
                      style={{ fontSize: FS_XS * fontScale, fontWeight: '600', color: option.color, ...ffs }}
                    >
                      {option.label}
                    </Text>
                  </Box>
                </TouchableOpacity>
              ))}
            </HStack>
          </VStack>
        )}

        {/* Voice placeholder */}
        {!completed && voiceEnabled && (
          <Box py="$3" alignItems="center">
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_SM * fontScale, color: t.textSecondary, textAlign: 'center', ...ffs }}
            >
              Tap the microphone to speak your response
            </Text>
          </Box>
        )}

        {/* Result */}
        {completed && score !== null && (
          <Box
            p="$2"
            rounded="$md"
            style={{ backgroundColor: score >= 70 ? '#D1FAE5' : '#FEF3C7' }}
          >
            <Text
              allowFontScaling={false}
              style={{
                fontSize: FS_SM * fontScale,
                fontWeight: '600',
                color: score >= 70 ? '#065F46' : '#92400E',
                textAlign: 'center',
                ...ffs,
              }}
            >
              {score >= 70 ? '✓ Great response!' : 'Keep practicing!'}
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
});
